import { useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';

import {
  useGetMemberAttachmentUrlsQuery,
  useGetCprMemberActivitiesInProgressByKeyQuery,
  GetCprMemberActivitiesInProgressByKeyQuery
} from 'src/graphql/generated';
import { useErrorHandler } from 'src/hooks/useErrorHandler';

export const useMemberActivity = ({
  activityKey,
  memberId,
  skip
}: {
  memberId: string | undefined | null;
  activityKey?: string;
  skip: boolean;
}) => {
  const handleError = useErrorHandler();
  const [data, setData] = useState<GetCprMemberActivitiesInProgressByKeyQuery | undefined>();
  const [error, setError] = useState<ApolloError | undefined>();

  const { data: formData, loading } = useGetCprMemberActivitiesInProgressByKeyQuery({
    variables: { memberId: memberId, activityKey: activityKey },
    skip: !skip,
    onCompleted: setData,
    onError: (error) => {
      setError(error);
      if (!error?.message?.includes('Activity')) {
        handleError(error);
      }
    },
    fetchPolicy: 'cache-and-network'
  });

  const existingAttachments = data?.getCPRMemberActivitiesInProgressByKey?.attachments?.map(
    (attachment) => {
      const { url, __typename, ...rest } = attachment!;
      return {
        ...rest
      };
    }
  );

  const { data: memberAttachmentsUrls } = useGetMemberAttachmentUrlsQuery({
    variables: { memberid: memberId, attachments: existingAttachments },
    skip: !data && !existingAttachments
  });

  const memberActivityData: GetCprMemberActivitiesInProgressByKeyQuery | undefined = useMemo(() => {
    return memberAttachmentsUrls && formData?.getCPRMemberActivitiesInProgressByKey
      ? {
          getCPRMemberActivitiesInProgressByKey: {
            ...formData.getCPRMemberActivitiesInProgressByKey,
            attachments: memberAttachmentsUrls?.getMemberAttachmentsUrls
          }
        }
      : undefined;
  }, [data?.getCPRMemberActivitiesInProgressByKey, memberAttachmentsUrls]);

  return { memberActivityData, error, loading };
};
